import { Repository, Result, ResultFailureFromApiError, ResultSuccess } from '@headless-workspace/core-domain';
import {
    AuthorizationStrategy,
    memoryCacheAuthStrategy,
    memoryCacheQueryParamStrategy,
    memoryCacheRefreshTokenStrategy,
    QueryParamStrategy,
    RefreshTokenStrategy,
} from '@headless-workspace/data';
import { ProductFormTemplateDTO } from './ProductFormDTO';
import {
    EngraveFormValue,
    mapProductTemplateForm,
    ProductFormId,
    ProductFormParams,
    ProductFormTemplateValue,
    RatingAndReviewsFormValue,
} from './ProductFormValues';

export interface ProductFormRepositorySpec {
    getProductFormById(id: ProductFormId.Review, params: ProductFormParams): Promise<Result<RatingAndReviewsFormValue>>;
    getProductFormById(id: ProductFormId.Engrave, params: ProductFormParams): Promise<Result<ProductFormTemplateValue>>;
}

export class ProductFormRepository extends Repository implements ProductFormRepositorySpec {
    path = '/content/v1';

    constructor(
        readonly authStrategy: Partial<AuthorizationStrategy> = memoryCacheAuthStrategy,
        readonly refreshTokenStrategy: Partial<RefreshTokenStrategy> = memoryCacheRefreshTokenStrategy,
        readonly queryParamStrategy: Partial<QueryParamStrategy> = memoryCacheQueryParamStrategy,
    ) {
        super(authStrategy, refreshTokenStrategy, queryParamStrategy);
    }

    // Overloads so the return type matches the product form id
    getProductFormById(id: ProductFormId.Review, params: ProductFormParams): Promise<Result<RatingAndReviewsFormValue>>;
    getProductFormById(id: ProductFormId.Engrave, params: ProductFormParams): Promise<Result<EngraveFormValue>>;
    getProductFormById(id: ProductFormId, params: ProductFormParams): Promise<Result<ProductFormTemplateValue>>;

    // Implementation of the overloaded method
    async getProductFormById(
        id: ProductFormId.Review | ProductFormId.Engrave,
        params: ProductFormParams,
    ): Promise<Result<RatingAndReviewsFormValue | EngraveFormValue | ProductFormTemplateValue>> {
        try {
            const response = await this.datasource.getResource(`${this.path}/forms/${id}`, params);
            const dto = ProductFormTemplateDTO.parse(response);

            const form = mapProductTemplateForm(id, dto);

            return ResultSuccess(form);
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { ...params });
        }
    }
}
